/**
 * Get a seek out of a hole in the recording.
 *
 * Around every segment boundary of the unified playlist there is a stretch hls.js cannot load
 * — measured on a node: from ~11 s before a segment's end to 2–7 s into the next one (18 s
 * at worst). Straight
 * playback runs through it, but a SEEK that lands inside it never completes: the video sits in
 * `seeking` with nothing buffered, forever. Events and "footage resumes" points are segment
 * starts, so jumping to one (with a lead-in) lands in exactly that stretch.
 *
 * So: after a seek, if the video is still stuck `waitMs` later, step forward `stepS` and look
 * again, up to `tries` times. Returns a cancel function — call it on the next seek.
 */
export function nudgeStalledSeek(
  video: HTMLVideoElement,
  { stepS = 3, tries = 7, waitMs = 1200 }: { stepS?: number; tries?: number; waitMs?: number } = {},
): () => void {
  let left = tries;
  let timer = 0;
  const check = () => {
    const stuck = video.seeking && video.readyState < 2;
    if (!stuck || left-- <= 0) return;
    const dur = Number.isFinite(video.duration) ? video.duration : Infinity;
    const next = Math.min(video.currentTime + stepS, Math.max(0, dur - 0.5));
    if (next <= video.currentTime) return;
    try { video.currentTime = next; } catch { return; }
    timer = window.setTimeout(check, waitMs);
  };
  timer = window.setTimeout(check, waitMs);
  return () => window.clearTimeout(timer);
}
